import { Box, Icon, Text } from "@salestrak/ui";

import { EntityGroupTag, EntityTag } from "#app/components/entity";
import type { EntityData } from "#app/lib/entity-visuals";
import { useQuickFilterSummary } from "../use-quick-filter-summary";
import {
  unfilteredFace,
  type QuickFilterOption,
  type QuickFilterSection,
} from "./quick-filter-options";

/** Named values shown in full before the rest fold behind a count. A row's
 *  value slot is a glance, not a list. */
const FACES_SHOWN = 2;

type QuickFilterValueProps = {
  section: QuickFilterSection;
  value: unknown;
};

/**
 * What a filter row reads on its trailing edge.
 *
 * A row states its value as the VALUE, not as a sentence about it: three
 * assignees are three avatars, two follow-up states are their two coloured
 * glyphs. A column of rows all reading "3 selected" is a column that has to be
 * opened one row at a time to learn anything, which is the whole reason the
 * value sits out here.
 *
 * Nothing applied is still a fact about the view, so the slot names the
 * unfiltered state ("Anyone", "Any team") in the faint rung rather than
 * sitting empty.
 */
export function QuickFilterValue({ section, value }: QuickFilterValueProps) {
  const applied = section.options.filter((option) => option.selected(value));
  // A value the index cannot name — a custom date range, a typed string, an id
  // whose entity is gone — still owes the row a readout.
  const summary = useQuickFilterSummary(section.def.type, value);

  if (applied.length === 0) {
    return (
      <Text size="sm" variant={summary ? "muted" : "faint"} truncate={1}>
        {summary ?? section.def.unfilteredFace ?? unfilteredFace(section.def.type)}
      </Text>
    );
  }

  const entities = applied
    .map((option) => option.entity)
    .filter((entity): entity is EntityData => !!entity);
  const named = applied.filter((option) => !option.entity);

  // The avatars ARE the readout, and the group's own "+N" carries the
  // overflow. A named value beside them (Unassigned with two people) reads
  // first, as the word it is, and the faces follow.
  if (entities.length > 0) {
    const [only] = entities;
    const faces =
      entities.length === 1 && only ? (
        <EntityTag
          entity={only}
          size="xs"
          weight="regular"
          interactive={false}
          // Beside a named value the row has one label already; the face
          // alone says who.
          display={named.length > 0 ? "visual" : undefined}
        />
      ) : (
        <EntityGroupTag
          entities={entities}
          size="xs"
          max={3}
          surface="popover"
          interactive={false}
          tooltip
          hoverDisabled
        />
      );
    if (named.length === 0) return faces;
    return (
      <Box direction="row" align="center" gap="xs" shrink minWidth={0}>
        {named.map((option) => (
          <NamedFace key={option.key} option={option} />
        ))}
        {faces}
      </Box>
    );
  }

  const shown = applied.slice(0, FACES_SHOWN);
  const hidden = applied.length - shown.length;

  return (
    <Box direction="row" align="center" gap="xs" shrink minWidth={0}>
      {shown.map((option) => (
        <NamedFace key={option.key} option={option} />
      ))}
      {hidden > 0 ? (
        <Text size="sm" variant="muted">
          {`+${hidden}`}
        </Text>
      ) : null}
    </Box>
  );
}

/** One named state, carrying its own hue — a follow-up chip on the row looks
 *  like the clock it filters. */
function NamedFace({ option }: { option: QuickFilterOption }) {
  return (
    <Box direction="row" align="center" gap="2xs" shrink minWidth={0}>
      {option.icon ? (
        <Icon name={option.icon} size="sm" color={option.iconColor} />
      ) : null}
      <Text size="sm" truncate={1}>
        {option.label}
      </Text>
    </Box>
  );
}
